import React from "react"
import { Alert, Text } from "@mantine/core"
import { IconAlertTriangle, IconLock } from "@tabler/icons-react"
import { LoginError } from "@models/error/login-error"
import { FORGEROCK_ERROR_CODES } from "@utils/constants.util"
import { stylizeText } from "@utils/stylizeText"
import { ForgeRockFormProps } from "@models/forgeRock.model"

interface ForgeRockErrorDisplayProps {
	error: LoginError
	loginFormComponentData?: ForgeRockFormProps["loginFormComponentData"]
	onAssistanceModalOpen?: () => void
}

export const ForgeRockErrorDisplay: React.FC<ForgeRockErrorDisplayProps> = ({
	error,
	loginFormComponentData,
	onAssistanceModalOpen
}) => {
	const getErrorIcon = () => {
		return error?.errorCode === FORGEROCK_ERROR_CODES.ACCOUNT_LOCKED ? (
			<IconLock />
		) : (
			<IconAlertTriangle />
		)
	}

	const getErrorMessage = (error: LoginError) => {
		if (error.errorCode === FORGEROCK_ERROR_CODES.INVALID_USERNAME) {
			return (
				<Text fz={12}>{loginFormComponentData?.unsuccessfulLoginMessage}</Text>
			)
		}

		if (error.errorCode === FORGEROCK_ERROR_CODES.INVALID_CREDENTIALS) {
			return (
				<>
					<Text fz={12}>
						{loginFormComponentData?.unsuccessfulLoginMessage}
					</Text>
					<Text fz={12} fw={600}>
						{error.remainingAttempts ?? ""}{" "}
						{loginFormComponentData?.attemptsRemaining}
					</Text>
				</>
			)
		}

		if (error.errorCode === FORGEROCK_ERROR_CODES.ACCOUNT_LOCKED) {
			return (
				<>
					<Text fz={12}>
						{loginFormComponentData?.accountLockedHeader
							? stylizeText(loginFormComponentData.accountLockedHeader, {
									fz: 12
								})
							: ""}
					</Text>
					<Text fz={12}>
						{loginFormComponentData?.accountLockedMessage
							? stylizeText(loginFormComponentData.accountLockedMessage, {
									onAnchorClick: onAssistanceModalOpen,
									fz: 12
								})
							: ""}
					</Text>
				</>
			)
		}

		if (error.errorCode === FORGEROCK_ERROR_CODES.QAMODE) {
			return (
				<Text fz={12}>
					{loginFormComponentData?.qaModeMessage || error.message}
				</Text>
			)
		}

		if (error.errorCode === FORGEROCK_ERROR_CODES.INVALID_TOKEN) {
			return <Text fz={12}>{stylizeText(error.message, { fz: 12 })}</Text>
		}

		if (
			error.errorCode === FORGEROCK_ERROR_CODES.FORGEROCK_ERROR ||
			error.errorCode === FORGEROCK_ERROR_CODES.FORGEROCK_CALLBACK_ERROR
		) {
			return (
				<>
					<Text
						fz={12}
						fw={
							error.errorCode === FORGEROCK_ERROR_CODES.FORGEROCK_ERROR
								? 600
								: undefined
						}
					>
						{loginFormComponentData?.unsuccessfulLoginMessage}
					</Text>
					<Text fz={12}>{error.message}</Text>
				</>
			)
		}

		return loginFormComponentData?.[error?.errorCode] ?? error.message
	}

	return (
		<Alert variant="filled" color="red" icon={getErrorIcon()} radius={30}>
			{getErrorMessage(error)}
		</Alert>
	)
}
